import {
  formatMoney,
  type HostBookingSummary,
  type Locale,
  type ManualBookingInput,
  pickLocale,
} from '@bb/shared';
import { useInfiniteQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { DateTime } from 'luxon';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCurrentUser } from '@/features/auth/use-current-user';
import {
  useBlockTime,
  useCreateManualBooking,
  useEventTypes,
  useRescheduleHostBooking,
  useUnblockTime,
} from '@/features/booking/api';
import { CalendarNav, CalendarView } from '@/features/booking/calendar-view';
import { dayTitle, whenInSentence } from '@/features/booking/dates';
import { Modal } from '@/features/booking/modal';
import { useMembers } from '@/features/workspace/api';
import { ApiRequestError, api } from '@/lib/api';
import { btnPrimary, btnSecondary, inputCls } from '@/lib/ui';

/*
  Four ways of looking at the same bookings.

  The three lists answer "what is coming up"; the two grids answer "what does
  Tuesday look like". Both belong here, because they are the same appointments
  and a person switches between the questions constantly.
*/
type View = 'upcoming' | 'past' | 'cancelled' | 'day' | 'week' | 'month';
const LIST_VIEWS = ['upcoming', 'past', 'cancelled'] as const;
/*
  The calendars first, widest first: the month is where somebody starts — which
  days are busy, which are free — and they narrow from there. The lists are
  where they go to find one booking by name.
*/
const VIEWS = ['month', 'week', 'day', ...LIST_VIEWS] as const;

/** What the page opens on, and therefore the one view the URL leaves out. */
const DEFAULT_VIEW = 'month' as const;

const isView = (v: unknown): v is View => VIEWS.includes(v as View);

/**
 * The view to open on when the URL does not name one.
 *
 * Remembered per browser, because the URL only carries it along the one route
 * out of a booking — the "Back to Upcoming" button. The sidebar, the heading
 * link and anything else that points at the bare page dropped you back on the
 * calendar, which is not where you were.
 *
 * Reading it, rather than redirecting to it, keeps the URL honest: a view named
 * in the address still wins, and a link somebody shares still means what it
 * says.
 */
const LAST_VIEW_KEY = 'bb.bookings.view';

function rememberedView(): View {
  try {
    const stored = localStorage.getItem(LAST_VIEW_KEY);
    return isView(stored) ? stored : DEFAULT_VIEW;
  } catch {
    return DEFAULT_VIEW;
  }
}

function rememberView(view: View): void {
  try {
    localStorage.setItem(LAST_VIEW_KEY, view);
  } catch {
    /* private mode: the page still works, it just forgets */
  }
}
const isList = (v: View): v is (typeof LIST_VIEWS)[number] =>
  (LIST_VIEWS as readonly string[]).includes(v);

export const Route = createFileRoute('/app/booking/bookings/')({
  /*
    The view and the day are in the URL. A day can then be linked to, survives
    a reload, and the back button means what it looks like it means.
  */
  validateSearch: (s: Record<string, unknown>): { view?: View; date?: string } => ({
    /*
      The month is kept in the URL like any other view.

      It used to be left out, on the grounds that the page's own address should
      be the short one. That stopped working the day the view became sticky:
      pressing Month navigated to a URL naming no view, the remembered one was
      still in effect, and the tab you had just pressed did not come on.
    */
    ...(isView(s.view) ? { view: s.view } : {}),
    ...(typeof s.date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(s.date) ? { date: s.date } : {}),
  }),
  component: BookingsPage,
});

function BookingsPage() {
  const search = Route.useSearch();
  // Settled once per mount: reading localStorage on every render would make the
  // view jump if another tab changed it mid-visit.
  const [fallback] = useState(rememberedView);
  const view = search.view ?? fallback;
  const date = search.date;
  useEffect(() => {
    rememberView(view);
  }, [view]);
  const { t } = useTranslation(['booking', 'common']);
  const { data: user } = useCurrentUser();
  const { data: eventTypes = [] } = useEventTypes();
  const [eventTypeId, setEventTypeId] = useState('');
  const [q, setQ] = useState('');
  const [showManual, setShowManual] = useState(false);
  const locale = (user?.locale ?? 'en') as Locale;
  const zone = user?.timezone ?? 'UTC';

  const me = useCurrentUser();
  const { data: members = [] } = useMembers();
  const people = [
    ...(me.data ? [{ id: me.data.id, label: t('booking:eventTypes.fields.takenByMe') }] : []),
    ...members
      .filter((m) => m.status === 'active' && m.agentId)
      .map((m) => ({ id: m.agentId as string, label: m.name ?? m.email })),
  ];
  const [agentId, setAgentId] = useState('');
  const listView = isList(view);
  const gridMode = listView ? 'day' : (view as 'day' | 'week' | 'month');
  const today = DateTime.now().setZone(zone).toISODate() ?? '';
  /*
    A click on an empty hour asks what it meant before doing anything. Two
    reasonable answers — somebody is coming, or nobody may — and guessing the
    first makes blocking out an afternoon a trip to another screen.
  */
  const [picked, setPicked] = useState<{
    start: DateTime;
    agentId: string | null;
    /** Picked from a month square, which names a day and no particular hour. */
    wholeDay?: boolean;
    /** Time already taken out of the week, which the menu offers to put back. */
    blocked: { hour: boolean; day: boolean };
    /** Time there is to take out. Nothing to block on a Sunday or a holiday. */
    working: { hour: boolean; day: boolean };
  } | null>(null);
  const [prefill, setPrefill] = useState<{ start: DateTime; agentId: string | null } | null>(null);
  /** A move waiting to be confirmed: it emails the customer. */
  const [move, setMove] = useState<{ booking: HostBookingSummary; to: DateTime } | null>(null);
  const block = useBlockTime();
  const unblock = useUnblockTime();
  const reschedule = useRescheduleHostBooking();

  const query = useInfiniteQuery({
    enabled: listView,
    queryKey: ['booking', 'bookings', { view, eventTypeId, q }],
    queryFn: ({ pageParam }) => {
      const qs = new URLSearchParams({ view: listView ? view : 'upcoming' });
      if (eventTypeId) qs.set('eventTypeId', eventTypeId);
      if (q) qs.set('q', q);
      if (pageParam) qs.set('cursor', pageParam);
      return api<{ items: HostBookingSummary[]; nextCursor: string | null }>(`/v1/bookings?${qs}`);
    },
    initialPageParam: '',
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const items = query.data?.pages.flatMap((p) => p.items) ?? [];

  // Group by local day in the host's zone.
  const groups = new Map<string, HostBookingSummary[]>();
  for (const b of items) {
    const key = DateTime.fromISO(b.start, { zone }).toISODate() ?? '';
    groups.set(key, [...(groups.get(key) ?? []), b]);
  }

  return (
    /*
      One width for every view, and the list kept narrow inside it.

      The page used to be narrow for the lists and wide for the grids, so
      switching between them moved the tabs, the filters and the Add booking
      button under the pointer — you pressed Upcoming and everything slid
      sideways. The frame stays put now; a list simply does not fill it,
      because a column of rows reads badly when it is seven days wide.
    */
    <div className="max-w-6xl space-y-4">
      {/*
        Two rows rather than one.

        Everything shared a line, with the search box taking what was left, so
        the event type dropdown was squeezed between the tabs and the search
        until its names were unreadable. What a person is looking at goes on
        top with the one action; what narrows the list sits underneath, where
        both filters have room.
      */}
      {/*
        The controls sit over whatever they control.

        On a list that is a column three units wide, so the search box and Add
        booking end at the same edge as the rows underneath them rather than
        stretching across a page the list does not use.
      */}
      <div className={`space-y-3 ${listView ? 'max-w-3xl' : ''}`}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          {/* Six tabs do not fit across a phone, so the row scrolls rather
              than hiding the last of them off the edge. */}
          <nav className="flex flex-1 gap-1 overflow-x-auto rounded-lg bg-neutral-100 p-1 text-sm sm:flex-none">
            {VIEWS.map((v) => (
              <Link
                key={v}
                to="/app/booking/bookings"
                search={
                  // Always naming the view is what makes pressing a tab mean
                  // the same thing whatever was remembered. The day comes too,
                  // so switching between grids keeps you on the day you were
                  // looking at.
                  isList(v) ? { view: v } : { view: v, date: date ?? today }
                }
                className={`flex min-h-11 shrink-0 flex-1 items-center justify-center whitespace-nowrap rounded px-3 sm:min-h-8 sm:flex-none ${
                  view === v ? 'bg-white font-medium shadow-sm' : 'text-neutral-600'
                }`}
              >
                {t(`booking:bookings.views.${v}`)}
              </Link>
            ))}
          </nav>
          <button
            type="button"
            onClick={() => setShowManual((s) => !s)}
            // Full width on a phone, where it wraps onto a line of its own and
            // a button sitting in the corner of it looks like an accident.
            className={`${btnPrimary} w-full shrink-0 whitespace-nowrap sm:w-auto`}
          >
            {t('booking:bookings.newManual')}
          </button>
        </div>
        <div className="flex flex-col gap-3 sm:flex-row">
          <select
            value={eventTypeId}
            onChange={(e) => setEventTypeId(e.target.value)}
            className={`${inputCls} sm:w-56`}
          >
            <option value="">{t('booking:bookings.allEventTypes')}</option>
            {eventTypes.map((et) => (
              <option key={et.id} value={et.id}>
                {pickLocale(et.name, locale)}
              </option>
            ))}
          </select>
          {/* Searching is a list's question. On a grid the second control is
              which person's day you are looking at. */}
          {listView ? (
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t('booking:bookings.search')}
              className={`${inputCls} sm:flex-1`}
            />
          ) : people.length > 1 ? (
            <select
              value={agentId}
              onChange={(e) => setAgentId(e.target.value)}
              className={`${inputCls} sm:w-56`}
            >
              <option value="">{t('booking:calendar.everyone')}</option>
              {people.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.label}
                </option>
              ))}
            </select>
          ) : null}
        </div>
      </div>

      {!listView ? (
        <CalendarNav mode={gridMode} date={date ?? today} zone={zone} locale={locale} />
      ) : null}

      {/*
        Over the page, not in it.

        These used to sit between the date pager and the grid, so answering one
        pushed the day you were looking at down the screen and it was never
        clear which of the three things on view was waiting for you.
      */}
      {showManual || prefill ? (
        <Modal
          wide
          title={t('booking:bookings.newManual')}
          onClose={() => {
            setShowManual(false);
            setPrefill(null);
          }}
        >
          <ManualBookingForm
            locale={locale}
            zone={zone}
            eventTypes={eventTypes.map((et) => ({ id: et.id, name: pickLocale(et.name, locale) }))}
            {...(prefill
              ? {
                  start: prefill.start,
                  ...(prefill.agentId ? { agentId: prefill.agentId } : {}),
                }
              : {})}
            onDone={() => {
              setShowManual(false);
              setPrefill(null);
            }}
          />
        </Modal>
      ) : null}

      {picked ? (
        <Modal
          title={picked.start
            .setLocale(locale)
            .toFormat(picked.wholeDay ? 'cccc d MMMM' : 'cccc d MMM, HH:mm')}
          onClose={() => setPicked(null)}
        >
          <SlotMenu
            busy={block.isPending || unblock.isPending}
            blocked={picked.blocked}
            working={picked.working}
            {...(picked.wholeDay ? { day: picked.start.toISODate() ?? '' } : {})}
            onBook={() => {
              setPrefill(picked);
              setPicked(null);
            }}
            onBlockHour={() => {
              const hour = {
                date: picked.start.toISODate() ?? '',
                from: picked.start.toFormat('HH:mm'),
                to: picked.start.plus({ hours: 1 }).toFormat('HH:mm'),
              };
              const run = picked.blocked.hour ? unblock : block;
              run.mutate(hour, { onSuccess: () => setPicked(null) });
            }}
            onBlockDay={() => {
              const day = { date: picked.start.toISODate() ?? '' };
              const run = picked.blocked.day ? unblock : block;
              run.mutate(day, { onSuccess: () => setPicked(null) });
            }}
            onCancel={() => setPicked(null)}
          />
        </Modal>
      ) : null}

      {move ? (
        <Modal
          title={t('booking:calendar.moveTitle')}
          onClose={() => {
            reschedule.reset();
            setMove(null);
          }}
        >
          <MoveConfirm
            booking={move.booking}
            to={move.to}
            zone={zone}
            locale={locale}
            busy={reschedule.isPending}
            error={reschedule.error instanceof ApiRequestError ? reschedule.error : null}
            onConfirm={() =>
              reschedule.mutate(
                { id: move.booking.id, start: move.to.toUTC().toISO() ?? '' },
                { onSuccess: () => setMove(null) },
              )
            }
            onCancel={() => {
              reschedule.reset();
              setMove(null);
            }}
          />
        </Modal>
      ) : null}

      {!listView ? (
        <CalendarView
          mode={gridMode}
          date={date ?? today}
          zone={zone}
          locale={locale}
          agentId={agentId}
          eventTypeId={eventTypeId}
          onAdd={(start, who, state) => {
            setShowManual(false);
            setPrefill(null);
            setPicked({
              start,
              agentId: who ?? (agentId || null),
              blocked: state.blocked,
              working: state.working,
              // A month square is a day; the grids pick a half hour inside one.
              ...(view === 'month' ? { wholeDay: true } : {}),
            });
          }}
          onMove={(booking, to) => setMove({ booking, to })}
          {...(picked ? { selected: { agentId: picked.agentId, at: picked.start } } : {})}
        />
      ) : null}

      {/* Left, not centred: the tabs above it are, and a column that drifts to
          the middle of a wide page reads as a different screen. */}
      <div className="max-w-3xl space-y-4">
        {listView && query.isLoading ? (
          <p className="text-sm text-neutral-500">{t('common:status.loading')}</p>
        ) : null}
        {listView && !query.isLoading && items.length === 0 ? (
          <p className="rounded-lg border border-dashed border-neutral-300 p-5 text-sm text-neutral-600">
            {t(`booking:bookings.empty.${view}`)}
          </p>
        ) : null}

        {[...groups.entries()].map(([day, list]) => (
          <section key={day}>
            <h2 className="mb-1.5 text-xs font-medium uppercase tracking-wide text-neutral-500">
              {dayTitle(DateTime.fromISO(day, { zone }), locale)}
            </h2>
            <ul className="divide-y divide-neutral-100 rounded-lg border border-neutral-200 bg-white">
              {list.map((b) => (
                <BookingRow key={b.id} booking={b} locale={locale} zone={zone} back={{ view }} />
              ))}
            </ul>
          </section>
        ))}

        {query.hasNextPage ? (
          <button
            type="button"
            onClick={() => void query.fetchNextPage()}
            disabled={query.isFetchingNextPage}
            className={btnSecondary}
          >
            {t('booking:bookings.loadMore')}
          </button>
        ) : null}
      </div>
    </div>
  );
}

function BookingRow({
  booking,
  locale,
  zone,
  back,
}: {
  booking: HostBookingSummary;
  locale: Locale;
  zone: string;
  /** The tab this row was clicked on, so the booking page can come back to it. */
  back: { view?: View };
}) {
  const { t } = useTranslation('booking');
  const s = DateTime.fromISO(booking.start, { zone }).setLocale(locale);
  const e = DateTime.fromISO(booking.end, { zone }).setLocale(locale);
  const emailFailed = Boolean(booking.bookerEmailFailed);
  const badge =
    booking.status === 'awaiting_payment'
      ? 'bg-amber-100 text-amber-800'
      : booking.status === 'cancelled' || booking.status === 'no_show'
        ? 'bg-neutral-200 text-neutral-700'
        : emailFailed ||
            booking.syncStatus === 'failed' ||
            booking.syncStatus === 'missing_externally'
          ? 'bg-red-100 text-red-800'
          : null;
  return (
    <li>
      <Link
        to="/app/booking/bookings/$id"
        params={{ id: booking.id }}
        search={back}
        className="flex items-center gap-3 px-4 py-3 text-sm hover:bg-neutral-50 sm:py-2.5"
      >
        <span
          className="h-10 w-1 shrink-0 rounded sm:h-8"
          style={{ backgroundColor: booking.eventTypeColour ?? '#a3a3a3' }}
          aria-hidden
        />
        <span className="hidden w-36 shrink-0 whitespace-nowrap tabular-nums text-neutral-700 sm:block">
          {s.toLocaleString(DateTime.TIME_SIMPLE)} – {e.toLocaleString(DateTime.TIME_SIMPLE)}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block truncate font-medium">
            <span className="tabular-nums text-neutral-700 sm:hidden">
              {s.toLocaleString(DateTime.TIME_SIMPLE)} – {e.toLocaleString(
                DateTime.TIME_SIMPLE,
              )}{' '}
            </span>
            {booking.bookerName}
          </span>
          <span className="block truncate text-xs text-neutral-500">
            {pickLocale(booking.eventTypeName, locale)}
            {/*
              Only when somebody else is taking it. Null means the account owner
              does, and their own name on every row of their own list is noise.
            */}
            {booking.agent ? ` · ${booking.agent.name}` : ''}
            {booking.amount && booking.currency
              ? ` · ${formatMoney({ amount: booking.amount, currency: booking.currency }, locale)}`
              : ''}
            {booking.source === 'manual' ? ` · ${t('bookings.source.manual')}` : ''}
          </span>
        </span>
        {badge ? (
          <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${badge}`}>
            {booking.status !== 'confirmed'
              ? t(`bookings.status.${booking.status}`)
              : emailFailed
                ? t('bookings.emailFailed.badge')
                : t(`bookings.sync.${booking.syncStatus}`)}
          </span>
        ) : null}
      </Link>
    </li>
  );
}

/**
 * What a click on an empty hour meant.
 *
 * Somebody is coming, or nobody may. Both are ordinary things to want from a
 * calendar, and guessing the first would make taking an afternoon off a trip
 * to the Availability screen.
 */
function SlotMenu({
  busy,
  blocked,
  working,
  day,
  onBook,
  onBlockHour,
  onBlockDay,
  onCancel,
}: {
  busy: boolean;
  /** What is already taken out, so the same buttons offer to put it back. */
  blocked: { hour: boolean; day: boolean };
  /** What there is to take out. A closed day has nothing. */
  working: { hour: boolean; day: boolean };
  /** Set when this came from a month square: a day, with no hour in it. */
  day?: string;
  onBook: () => void;
  onBlockHour: () => void;
  onBlockDay: () => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation(['booking', 'common']);
  // Stacked, because they are different answers rather than a toolbar, and the
  // one most people want is first.
  return (
    <div className="space-y-2">
      <button type="button" onClick={onBook} className={`${btnPrimary} w-full`}>
        {t('booking:calendar.addHere')}
      </button>
      {/*
        An hour nobody named cannot be blocked: a month square is a day, and the
        day number beside it is how you get into one.

        Nor can an hour that is not worked anyway — on a Sunday, a public
        holiday or a day already closed, "block this hour" does nothing and
        offering it suggests otherwise.
      */}
      {!day && (blocked.hour || working.hour) ? (
        <button
          type="button"
          onClick={onBlockHour}
          disabled={busy}
          className={`${btnSecondary} w-full`}
        >
          {t(blocked.hour ? 'booking:calendar.unblockHour' : 'booking:calendar.blockHour')}
        </button>
      ) : null}
      {/* Same for the day: something to take out, or something to put back. */}
      {blocked.day || working.day ? (
        <button
          type="button"
          onClick={onBlockDay}
          disabled={busy}
          className={`${btnSecondary} w-full`}
        >
          {t(blocked.day ? 'booking:calendar.unblockDay' : 'booking:calendar.blockDay')}
        </button>
      ) : null}
      <button type="button" onClick={onCancel} className={`${btnSecondary} w-full`}>
        {t('common:actions.cancel')}
      </button>
    </div>
  );
}

/**
 * A dragged booking, before the customer hears about it.
 *
 * Dropping something is easy to do by accident and this one sends an email
 * saying their appointment has moved. So the drag proposes and the person
 * confirms, with both times spelled out.
 */
function MoveConfirm({
  booking,
  to,
  zone,
  locale,
  busy,
  error,
  onConfirm,
  onCancel,
}: {
  booking: HostBookingSummary;
  to: DateTime;
  zone: string;
  locale: Locale;
  busy: boolean;
  error: ApiRequestError | null;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation(['booking', 'common', 'errors']);
  const from = DateTime.fromISO(booking.start).setZone(zone).setLocale(locale);
  return (
    <div>
      <p className="text-sm">
        {t('booking:calendar.moveQuestion', {
          name: booking.bookerName,
          from: whenInSentence(from, locale),
          to: whenInSentence(to, locale),
        })}
      </p>
      <p className="mt-1 text-xs text-neutral-500">{t('booking:calendar.moveEmails')}</p>
      {error ? <p className="mt-1 text-xs text-red-600">{t(`errors:${error.code}`)}</p> : null}
      <div className="mt-3 flex gap-2">
        <button type="button" onClick={onConfirm} disabled={busy} className={btnPrimary}>
          {t('booking:calendar.moveConfirm')}
        </button>
        <button type="button" onClick={onCancel} className={btnSecondary}>
          {t('common:actions.cancel')}
        </button>
      </div>
    </div>
  );
}

function ManualBookingForm(props: {
  locale: Locale;
  zone: string;
  eventTypes: Array<{ id: string; name: string }>;
  /** Where a click on the grid landed, if that is what opened this. */
  start?: DateTime;
  agentId?: string;
  onDone: () => void;
}) {
  const { t } = useTranslation(['booking', 'common', 'errors']);
  const create = useCreateManualBooking();
  const [eventTypeId, setEventTypeId] = useState(props.eventTypes[0]?.id ?? '');
  const [date, setDate] = useState(
    (props.start ?? DateTime.now().setZone(props.zone)).toISODate() ?? '',
  );
  const [time, setTime] = useState(props.start?.toFormat('HH:mm') ?? '10:00');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [notes, setNotes] = useState('');
  const [notify, setNotify] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [conflict, setConflict] = useState(false);
  /*
    Empty means the rotation decides, which is what the booking page would have
    done and so the right default. Naming somebody is for the appointment the
    owner has already promised to a particular person.
  */
  const [agentId, setAgentId] = useState(props.agentId ?? '');
  const me = useCurrentUser();
  const { data: members = [] } = useMembers();
  const people = [
    ...(me.data ? [{ id: me.data.id, label: t('booking:eventTypes.fields.takenByMe') }] : []),
    ...members
      .filter((m) => m.status === 'active' && m.agentId)
      .map((m) => ({ id: m.agentId as string, label: m.name ?? m.email })),
  ];

  const submit = (force: boolean) => {
    setError(null);
    const start = DateTime.fromISO(`${date}T${time}`, { zone: props.zone }).toUTC().toISO();
    if (!start || !eventTypeId) return;
    const input: ManualBookingInput = {
      eventTypeId,
      start,
      booker: {
        name,
        email,
        ...(phone ? { phone } : {}),
        locale: props.locale,
        timezone: props.zone,
      },
      ...(notes ? { notes } : {}),
      ...(agentId ? { agentId } : {}),
      force,
      notify,
    };
    create.mutate(input, {
      onSuccess: props.onDone,
      onError: (err) => {
        const code = err instanceof ApiRequestError ? err.code : 'generic';
        if (code === 'slot_conflict') setConflict(true);
        else setError(code);
      },
    });
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        submit(false);
      }}
      className="space-y-3"
    >
      <div className="grid gap-3 sm:grid-cols-3">
        <select
          value={eventTypeId}
          onChange={(e) => setEventTypeId(e.target.value)}
          className={inputCls}
          required
        >
          {props.eventTypes.map((et) => (
            <option key={et.id} value={et.id}>
              {et.name}
            </option>
          ))}
        </select>
        <input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          className={inputCls}
          required
        />
        <input
          type="time"
          value={time}
          step={300}
          onChange={(e) => setTime(e.target.value)}
          className={inputCls}
          required
        />
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={t('booking:bookings.bookerName')}
          className={inputCls}
          required
        />
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={t('booking:public.form.email')}
          className={inputCls}
          required
        />
        <input
          type="tel"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder={t('booking:public.form.phone')}
          className={inputCls}
        />
      </div>
      {/*
        Only when there is a choice. One person in the business means one
        possible answer, and a dropdown offering it is a question with no point.
      */}
      {people.length > 1 ? (
        <label className="block text-sm">
          <span className="mb-1 block text-neutral-600">{t('booking:bookings.takenByLabel')}</span>
          <select value={agentId} onChange={(e) => setAgentId(e.target.value)} className={inputCls}>
            <option value="">{t('booking:bookings.takenByAnyone')}</option>
            {people.map((p) => (
              <option key={p.id} value={p.id}>
                {p.label}
              </option>
            ))}
          </select>
        </label>
      ) : null}
      <textarea
        rows={2}
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        placeholder={t('booking:bookings.notesPlaceholder')}
        className={inputCls}
      />
      <label className="flex min-h-11 items-center gap-2 text-sm sm:min-h-0">
        <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} />
        {t('booking:bookings.notifyBooker')}
      </label>
      {conflict ? (
        <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          {t('booking:bookings.conflictWarning')}{' '}
          <button type="button" onClick={() => submit(true)} className="font-medium underline">
            {t('booking:bookings.bookAnyway')}
          </button>
        </div>
      ) : null}
      {error ? <p className="text-sm text-red-600">{t(`errors:${error}`)}</p> : null}
      <div className="flex gap-2">
        <button type="submit" disabled={create.isPending} className={btnPrimary}>
          {t('common:actions.save')}
        </button>
        <button type="button" onClick={props.onDone} className={btnSecondary}>
          {t('common:actions.cancel')}
        </button>
      </div>
    </form>
  );
}
